import { apiClient } from '@/core/api/client';
import type {
  MondayMeetingTask,
  MondayMeetingComment,
  MondayMeetingAttachment,
  MondayMeetingCategory,
  MondayMeetingStats,
  MondayMeetingFilters,
  CreateTaskData,
  UpdateTaskData,
  User,
  PaginatedResponse,
  WeeksResponse,
} from '../types/monday-meeting.types';

const BASE_URL = '/api/admin/monday-meeting';

// =============================================================================
// Stats & Metadata
// =============================================================================

export async function getStats(): Promise<{ success: boolean; data: MondayMeetingStats }> {
  return apiClient.get(`${BASE_URL}/stats`);
}

export async function getWeeks(): Promise<WeeksResponse> {
  return apiClient.get(`${BASE_URL}/weeks`);
}

export async function getUsers(): Promise<{ success: boolean; data: User[] }> {
  return apiClient.get(`${BASE_URL}/users`);
}

// =============================================================================
// Categories
// =============================================================================

export async function getCategories(): Promise<{ success: boolean; data: MondayMeetingCategory[] }> {
  return apiClient.get(`${BASE_URL}/categories`);
}

export async function createCategory(name: string): Promise<{ success: boolean; data: MondayMeetingCategory }> {
  return apiClient.post(`${BASE_URL}/categories`, { name });
}

export async function updateCategory(id: string, data: { name?: string; sort_order?: number }): Promise<{ success: boolean; data: MondayMeetingCategory }> {
  return apiClient.put(`${BASE_URL}/categories/${id}`, data);
}

export async function deleteCategory(id: string): Promise<{ success: boolean; message: string }> {
  return apiClient.delete(`${BASE_URL}/categories/${id}`);
}

// =============================================================================
// Tasks CRUD
// =============================================================================

export async function getTasks(filters: MondayMeetingFilters = {}): Promise<PaginatedResponse<MondayMeetingTask>> {
  const params = new URLSearchParams();

  if (filters.year) params.append('year', filters.year.toString());
  if (filters.week) params.append('week', filters.week.toString());
  if (filters.created_year) params.append('created_year', filters.created_year.toString());
  if (filters.created_week) params.append('created_week', filters.created_week.toString());
  if (filters.status) params.append('status', filters.status);
  if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters.category_id) params.append('category_id', filters.category_id);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}/tasks?${queryString}` : `${BASE_URL}/tasks`;

  return apiClient.get(url);
}

export async function getTaskById(id: string): Promise<{ success: boolean; data: MondayMeetingTask }> {
  return apiClient.get(`${BASE_URL}/tasks/${id}`);
}

export async function createTask(data: CreateTaskData): Promise<{ success: boolean; message: string; data: MondayMeetingTask }> {
  return apiClient.post(`${BASE_URL}/tasks`, data);
}

export async function updateTask(id: string, data: UpdateTaskData): Promise<{ success: boolean; message: string; data: MondayMeetingTask }> {
  return apiClient.put(`${BASE_URL}/tasks/${id}`, data);
}

export async function deleteTask(id: string): Promise<{ success: boolean; message: string }> {
  return apiClient.delete(`${BASE_URL}/tasks/${id}`);
}

// =============================================================================
// Comments
// =============================================================================

export async function getComments(taskId: string): Promise<{ success: boolean; data: MondayMeetingComment[] }> {
  return apiClient.get(`${BASE_URL}/tasks/${taskId}/comments`);
}

export async function addComment(taskId: string, content: string): Promise<{ success: boolean; message: string; data: MondayMeetingComment }> {
  return apiClient.post(`${BASE_URL}/tasks/${taskId}/comments`, { content });
}

export async function deleteComment(commentId: string): Promise<{ success: boolean; message: string }> {
  return apiClient.delete(`${BASE_URL}/comments/${commentId}`);
}

// =============================================================================
// Attachments
// =============================================================================

export async function getTaskAttachments(taskId: string): Promise<{ success: boolean; data: MondayMeetingAttachment[] }> {
  return apiClient.get(`${BASE_URL}/tasks/${taskId}/attachments`);
}

export async function uploadTaskAttachments(
  taskId: string,
  files: File[],
  onProgress?: (percent: number) => void
): Promise<{ success: boolean; message: string; data: MondayMeetingAttachment[] }> {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  // Get auth token
  const token = localStorage.getItem('authToken');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be'}${BASE_URL}/tasks/${taskId}/attachments`);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(new Error(xhr.statusText || 'Upload failed'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(formData);
  });
}

export function getAttachmentDownloadUrl(attachmentId: string, inline = false): string {
  const token = localStorage.getItem('authToken');
  const baseUrl = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
  return `${baseUrl}${BASE_URL}/attachments/${attachmentId}/download?inline=${inline}&token=${token}`;
}

export async function downloadAttachment(attachmentId: string, fileName: string): Promise<void> {
  const token = localStorage.getItem('authToken');
  const baseUrl = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
  const url = `${baseUrl}${BASE_URL}/attachments/${attachmentId}/download`;

  console.log('[Download] Starting:', { attachmentId, fileName, url, hasToken: !!token });

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  console.log('[Download] Response:', { status: response.status, ok: response.ok, contentType: response.headers.get('content-type') });

  if (!response.ok) {
    console.error('[Download] Failed:', response.status, response.statusText);
    throw new Error('Download failed');
  }

  const blob = await response.blob();
  console.log('[Download] Blob received:', { type: blob.type, size: blob.size });

  const blobUrl = window.URL.createObjectURL(blob);
  console.log('[Download] Triggering download with blob URL');

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(blobUrl);
  document.body.removeChild(a);

  console.log('[Download] Completed');
}

export async function deleteAttachment(attachmentId: string): Promise<{ success: boolean; message: string }> {
  return apiClient.delete(`${BASE_URL}/attachments/${attachmentId}`);
}

// =============================================================================
// Helpers
// =============================================================================

export function getWeekLabel(year: number, week: number): string {
  return `S${week} - ${year}`;
}

export function getRelativeWeekLabel(weeksUntilDue: number, dueWeek: number): string {
  if (weeksUntilDue < 0) {
    return `En retard (${weeksUntilDue} sem.)`;
  }
  if (weeksUntilDue === 0) {
    return `Cette semaine (S${dueWeek})`;
  }
  return `+${weeksUntilDue} sem. (S${dueWeek})`;
}

export function getCurrentISOWeek(): { year: number; week: number } {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);

  // Handle ISO week year
  let year = now.getFullYear();
  if (now.getMonth() === 0 && weekNumber > 50) {
    year--;
  } else if (now.getMonth() === 11 && weekNumber === 1) {
    year++;
  }

  return { year, week: weekNumber };
}

export function generateFutureWeeks(count: number = 5): Array<{ year: number; week: number; label: string }> {
  const { year, week } = getCurrentISOWeek();
  const weeks = [];

  for (let i = 0; i < count; i++) {
    let targetWeek = week + i;
    let targetYear = year;

    // Handle year transition (assuming max 53 weeks per year)
    while (targetWeek > 53) {
      targetWeek -= 53;
      targetYear++;
    }

    const label = i === 0
      ? `Cette semaine (S${targetWeek})`
      : `+${i} sem. (S${targetWeek})`;

    weeks.push({ year: targetYear, week: targetWeek, label });
  }

  return weeks;
}
